import { useQuery } from "@apollo/client";

import {
  GET_OWN_OEM_ASSET_TEMPLATE_BY_ID,
  GET_SHARED_ASSET_TEMPLATE_BY_ID,
  LIST_OWN_OEM_ASSET_TEMPLATE_QUERY,
  LIST_OWN_OEM_ASSET_TEMPLATE_WITH_COUNT_QUERY,
  LIST_SHARED_OEM_ASSET_TEMPLATE_QUERY,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants";
import { AssetTemplate } from "~/types/assetTemplate";
import { generateAssetTemplateQueryWhereCond } from "~/utils/filter/assetTemplateQueryCond";

export const useGetOwnOemAssetTemplateById = (
  templateId: string,
  skipCondition = false,
  isSharedTemplate = false,
) => {
  const {
    data: { getOwnOemAssetTemplate = {} as AssetTemplate } = {},
    loading,
    error,
  } = useQuery<{ getOwnOemAssetTemplate: AssetTemplate }>(
    isSharedTemplate
      ? GET_SHARED_ASSET_TEMPLATE_BY_ID
      : GET_OWN_OEM_ASSET_TEMPLATE_BY_ID,
    {
      variables: { templateId },
      skip: skipCondition,
    },
  );

  return {
    loading,
    error,
    template: getOwnOemAssetTemplate,
  };
};

export const useGetAssetTemplates = (
  params = {},
  activeFilters = {},
  skipCondition = false,
  sort: string[] = [],
) => {
  const filterConditions = generateAssetTemplateQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const sortParam = Array.isArray(sort) && sort.length > 0 ? sort : undefined;

  const variables = {
    params: {
      where: {
        ...(hasFilter ? filterConditions : {}),
        ...params,
      },
      ...(sortParam ? { sort: sortParam } : {}),
    },
  };

  const {
    data: { listOwnOemAssetTemplates = [] } = {},
    error,
    loading,
  } = useQuery<{ listOwnOemAssetTemplates: Array<AssetTemplate> }>(
    LIST_OWN_OEM_ASSET_TEMPLATE_QUERY,
    {
      variables,
      skip: skipCondition,
    },
  );

  return { listOwnOemAssetTemplates, error, loading };
};

export const useGetAssetTemplatesWithCount = ({
  params = {},
  limit = ITEMS_PER_PAGE,
  skip = 0,
  activeFilters = {},
  skipCondition = false,
}) => {
  const filterConditions = generateAssetTemplateQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;

  const variables = {
    params: {
      where: {
        ...(hasFilter ? filterConditions : {}),
        ...params,
      },
      limit,
      skip,
    },
  };

  const {
    data: {
      listOwnOemAssetTemplatesWithCount = {
        totalCount: 0,
        templates: [],
      },
    } = {},
    error,
    fetchMore,
    loading,
  } = useQuery<{
    listOwnOemAssetTemplatesWithCount: {
      templates: Array<AssetTemplate>;
      totalCount: number;
    };
  }>(LIST_OWN_OEM_ASSET_TEMPLATE_WITH_COUNT_QUERY, {
    variables,
    skip: skipCondition,
  });

  const handleFetchMore = ({
    limit,
    skip,
  }: {
    limit: number;
    skip: number;
  }): void => {
    const variables = {
      params: {
        where: {
          ...(hasFilter ? filterConditions : {}),
          ...params,
        },
        limit,
        skip,
      },
    };
    fetchMore({
      variables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTemplates = [
          ...previousResult.listOwnOemAssetTemplatesWithCount.templates,
          ...fetchMoreResult.listOwnOemAssetTemplatesWithCount.templates,
        ];

        return {
          ...previousResult,
          listOwnOemAssetTemplatesWithCount: {
            ...previousResult.listOwnOemAssetTemplatesWithCount,
            templates: mergedTemplates,
          },
        };
      },
    });
  };

  return {
    totalCount: listOwnOemAssetTemplatesWithCount?.totalCount,
    templates: listOwnOemAssetTemplatesWithCount?.templates,
    handleFetchMore,
    error,
    loading,
  };
};

export const useGetSharedAssetTemplates = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  orgId = "",
  sort = ["title:asc"],
  skipCondition = false,
}: {
  limit?: number;
  skip?: number;
  sort?: string[];
  searchQuery?: string;
  orgId?: string;
  skipCondition?: boolean;
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      sort,
      where: {
        searchQuery,
        orgId,
      },
    },
  };

  const { data, loading, refetch, fetchMore } = useQuery<
    {
      listSharedAssetTemplates: {
        currentPage: number;
        limit: number;
        skip: number;
        totalCount: number;
        templates: AssetTemplate[];
      };
    },
    {
      params: {
        limit: number;
        skip: number;
        sort?: string[];
        where: {
          searchQuery: string;
        };
      };
    }
  >(LIST_SHARED_OEM_ASSET_TEMPLATE_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    templates = [],
  } = data?.listSharedAssetTemplates ?? {
    currentPage: 0,
    limit: 0,
    skip: 0,
    totalCount: 0,
    templates: [],
  };

  const handleFetchMore = ({
    limit,
    skip,
  }: {
    limit: number;
    skip: number;
  }): void => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          sort,
          where: {
            searchQuery,
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTemplates = [
          ...previousResult.listSharedAssetTemplates.templates,
          ...fetchMoreResult.listSharedAssetTemplates.templates,
        ];

        return {
          ...previousResult,
          listSharedAssetTemplates: {
            ...previousResult.listSharedAssetTemplates,
            ...fetchMoreResult.listSharedAssetTemplates,
            templates: mergedTemplates,
          },
        };
      },
    });
  };

  return {
    templates,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};
